<template>
  <div class="proofreading-waiting">
    <div
      v-if="!isFailed"
      class="proofreading-waiting__center"
      :class="{ 'proofreading-waiting__center--stale': isStaleRefund }"
    >
      <CatIcon
        v-if="!isStaleRefund"
        size="large"
        :clickable="false"
        class="proofreading-waiting__cat"
        aria-hidden="true"
      />

      <p
        class="proofreading-waiting__message"
        :class="{ 'proofreading-waiting__message--stale': isStaleRefund }"
      >
        {{ statusMessage }}
      </p>

      <LoadingSpinner
        v-if="!isStaleRefund"
        size="medium"
        variant="inline"
        class="proofreading-waiting__spinner"
        aria-hidden="true"
      />

      <p v-if="isFinishing" class="proofreading-waiting__status">
        {{ $t("proofreadingWaitingPage.finishingHeading") }}
      </p>

      <button
        v-if="!isFinishing && !isCancelling"
        type="button"
        class="proofreading-waiting__cancel-button"
        @click="onClickCancel"
      >
        {{ $t("proofreadingWaitingPage.cancel") }}
      </button>
    </div>

    <div v-else class="proofreading-waiting__center proofreading-waiting__center--failed">
      <CatIcon
        size="large"
        :clickable="false"
        class="proofreading-waiting__cat"
        aria-hidden="true"
      />

      <p class="proofreading-waiting__message proofreading-waiting__message--failed">
        {{ statusMessage }}
      </p>

      <p class="proofreading-waiting__error">
        {{ $t("proofreadingWaitingPage.failedMessage") }}
      </p>

      <button
        type="button"
        class="proofreading-waiting__back-button"
        @click="onClickBackToType"
      >
        {{ $t("proofreadingWaitingPage.backToType") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import CatIcon from "@/components/atoms/CatIcon.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import ProofreadingAiCancelDialog, {
  ProofreadingAiCancelDialogResult,
} from "@/components/ui/dialogs/ProofreadingAiCancelDialog.vue";
import { Dialog } from "@/lib/utils/dialog";
import { showNotifyDialog } from "@/lib/dialog";
import {
  cancelProofreadingBatch,
  cancelStaleProofreadingBatch,
  finishProofreadingBatch,
  getProofreadingBatch,
} from "@/lib/api/proofreadingAi";
import { isStaleAiProofreadingRefund } from "@/lib/utils/proofreadingStaleRefund";
import { useProofreadingAiStore } from "@/stores/proofreadingAi";
import { startAiProofreadingBatchPolling } from "@/lib/utils/proofreadingAiPolling";
import type { AiProofreadingPollController } from "@/lib/utils/proofreadingAiPolling";
import { ProofreadingAnalytics } from "@/lib/utils/analytics/proofreading";

export default Vue.extend({
  components: {
    CatIcon,
    LoadingSpinner,
  },
  props: {
    novelId: String,
  },
  data() {
    return {
      proofreadingAiStore: useProofreadingAiStore(),
      pollController: null as AiProofreadingPollController | null,
      isFinishing: false,
      isCancelling: false,
      isFailed: false,
      analytics: null as ProofreadingAnalytics | null,
      batchCreatedAt: null as number | null,
      nowMs: Date.now(),
      staleClockId: null as ReturnType<typeof setInterval> | null,
    };
  },
  computed: {
    batchId(): string | null {
      return this.proofreadingAiStore.batchId;
    },
    batchStatus(): string | null {
      return this.proofreadingAiStore.batchStatus;
    },
    statusMessage(): string {
      if (this.isFailed) {
        return this.$t("proofreadingWaitingPage.failedSpeech") as string;
      }
      if (this.isFinishing) {
        return this.$t("proofreadingWaitingPage.completedSpeech") as string;
      }
      if (this.isStaleRefund) {
        return this.$t("proofreadingWaitingPage.staleMessage") as string;
      }
      return this.$t("proofreadingWaitingPage.speechMessage") as string;
    },
    isStaleRefund(): boolean {
      if (this.isFailed || this.isFinishing) return false;
      return isStaleAiProofreadingRefund(this.batchCreatedAt, this.nowMs);
    },
  },
  async mounted() {
    this.staleClockId = setInterval(() => {
      this.nowMs = Date.now();
    }, 1000);
    this.analytics = new ProofreadingAnalytics(this.$gtm);
    if (this.novelId) {
      this.proofreadingAiStore.setActiveNovelId(this.novelId);
      this.proofreadingAiStore.setMode("ai");
      this.proofreadingAiStore.loadBatchIdFromStorage(this.novelId);
    }

    if (!this.novelId || !this.batchId) {
      await this.redirectToTypeIfMissingBatch();
      return;
    }

    await this.pollOnce();
    this.startPolling();
  },
  beforeDestroy() {
    if (this.staleClockId != null) {
      clearInterval(this.staleClockId);
      this.staleClockId = null;
    }
    this.stopPolling();
  },
  methods: {
    stopPolling() {
      this.pollController?.stop();
      this.pollController = null;
    },
    startPolling() {
      if (!this.novelId || !this.batchId) return;

      this.pollController = startAiProofreadingBatchPolling({
        skipInitialPoll: true,
        poll: async () => {
          const response = await getProofreadingBatch(this.novelId!, this.batchId!);
          this.rememberBatchCreatedAt(response.createdAt);
          this.proofreadingAiStore.setBatchStatus(response.batchStatus);
          return response.batchStatus;
        },
        onStatus: (status) => {
          if (status === "RUNNING") {
            return true;
          }
          if (status === "FAILED") {
            this.handleFailed();
            return false;
          }
          if (status === "CANCELLED") {
            this.handleCancelled();
            return false;
          }
          if (status === "COMPLETED") {
            this.handleCompleted().catch((error) => {
              console.error("Failed to complete AI proofreading batch", error);
            });
            return false;
          }
          return false;
        },
        onError: (error) => {
          console.error("AI proofreading batch polling failed", error);
        },
      });
    },
    async pollOnce() {
      if (!this.novelId || !this.batchId) return;
      try {
        const response = await getProofreadingBatch(this.novelId, this.batchId);
        this.rememberBatchCreatedAt(response.createdAt);
        this.proofreadingAiStore.setBatchStatus(response.batchStatus);
        if (response.batchStatus === "FAILED") {
          this.handleFailed();
        } else if (response.batchStatus === "CANCELLED") {
          this.handleCancelled();
        } else if (response.batchStatus === "COMPLETED") {
          await this.handleCompleted();
        }
      } catch (error) {
        console.error("Failed to fetch AI proofreading batch", error);
      }
    },
    async handleCompleted() {
      if (!this.novelId || !this.batchId || this.isFinishing) return;

      this.stopPolling();
      this.isFinishing = true;

      try {
        const response = await finishProofreadingBatch(this.novelId, this.batchId);
        if (response.skipped === "waiting_gemini_batch") {
          this.isFinishing = false;
          this.startPolling();
          return;
        }

        if (response.result) {
          this.proofreadingAiStore.setAiResult(response.result);
        }
        this.analytics?.trackAiComplete({
          novelId: this.novelId,
          totalChars: this.proofreadingAiStore.totalChars,
          checkCount: this.proofreadingAiStore.enabledCheckCount,
          estimatedFish: this.proofreadingAiStore.estimatedFish,
          batchId: this.batchId,
        });
        this.proofreadingAiStore.setBatchStatus("COMPLETED");
        // AI結果の反映完了までは batchId を保持し、画面再読込時も結果を再取得可能にする。
        // 追跡情報は RevisingEditor の反映成功時に clearAiReviseSession が削除する。

        this.$router.replace({
          name: "proofreadingReviseManuscript",
          params: { novelId: this.novelId },
          query: { mode: "ai" },
        }).catch(() => undefined);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        await showNotifyDialog({ content: message });
        this.isFinishing = false;
        this.startPolling();
      }
    },
    handleFailed() {
      this.stopPolling();
      this.isFailed = true;
      if (this.novelId) {
        this.analytics?.trackAiFail({
          novelId: this.novelId,
          batchId: this.batchId ?? undefined,
        });
        this.proofreadingAiStore.clearBatchTracking(this.novelId);
      }
    },
    handleCancelled() {
      this.stopPolling();
      if (this.novelId) {
        this.proofreadingAiStore.clearBatchTracking(this.novelId);
      }
      this.$router.replace({
        name: "proofreadingSelectType",
        params: { novelId: this.novelId! },
      });
    },
    getHttpStatus(error: unknown): number | undefined {
      if (error && typeof error === "object" && "response" in error) {
        const {response} = error as { response?: { status?: number } };
        if (response && typeof response.status === "number") {
          return response.status;
        }
      }
      return undefined;
    },
    async redirectToTypeIfMissingBatch() {
      if (!this.novelId) return;
      await showNotifyDialog({
        content: this.$t("proofreadingWaitingPage.missingBatch") as string,
      });
      this.$router.replace({
        name: "proofreadingSelectType",
        params: { novelId: this.novelId },
      });
    },
    rememberBatchCreatedAt(createdAt?: number) {
      if (typeof createdAt === "number" && Number.isFinite(createdAt)) {
        this.batchCreatedAt = createdAt;
      }
      this.nowMs = Date.now();
    },
    async onClickCancel() {
      const refund = this.isStaleRefund;
      const confirmed = await new Dialog(ProofreadingAiCancelDialog).show({
        refund,
      }) as ProofreadingAiCancelDialogResult;
      if (!confirmed || !this.novelId || !this.batchId) return;

      this.isCancelling = true;
      try {
        const latest = await getProofreadingBatch(this.novelId, this.batchId);
        this.rememberBatchCreatedAt(latest.createdAt);
        if (latest.batchStatus === "COMPLETED") {
          await this.handleCompleted();
          return;
        }
        if (latest.batchStatus === "FAILED") {
          this.handleFailed();
          return;
        }
        const cancelResponse = refund
          ? await cancelStaleProofreadingBatch(this.novelId, this.batchId)
          : await cancelProofreadingBatch(this.novelId, this.batchId);
        this.stopPolling();
        this.analytics?.trackAiCancelUi({
          novelId: this.novelId,
          batchId: this.batchId,
        });
        this.proofreadingAiStore.clearBatchTracking(this.novelId);
        await this.$store.dispatch("fishModule/fetchFishData");
        if (cancelResponse.refundedFish > 0) {
          const amount = cancelResponse.refundedFish.toLocaleString(this.$i18n.locale || "ja");
          await showNotifyDialog({
            content: this.$t("proofreadingWaitingPage.refundedFish", { amount }) as string,
          });
        }
        this.$router.push({
          name: "proofreadingSelectType",
          params: { novelId: this.novelId },
        });
      } catch (error) {
        const status = this.getHttpStatus(error);
        if (status === 409) {
          const response = await getProofreadingBatch(this.novelId, this.batchId);
          if (response.batchStatus === "COMPLETED") {
            await this.handleCompleted();
            return;
          }
          if (response.batchStatus === "FAILED") {
            this.handleFailed();
            return;
          }
        }
        if (status === 404) {
          this.stopPolling();
          this.proofreadingAiStore.clearBatchTracking(this.novelId);
          this.$router.push({
            name: "proofreadingSelectType",
            params: { novelId: this.novelId },
          });
          return;
        }
        const message = error instanceof Error ? error.message : String(error);
        await showNotifyDialog({ content: message });
      } finally {
        this.isCancelling = false;
      }
    },
    onClickBackToType() {
      if (!this.novelId) return;
      this.$router.push({
        name: "proofreadingSelectType",
        params: { novelId: this.novelId },
      });
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.proofreading-waiting {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 48px 24px;
  color: var(--color-text-normal);
  background-color: var(--color-surface-default, #fff);
}

.proofreading-waiting__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 360px;
  margin-top: -190px;
  text-align: center;
}

.proofreading-waiting__center--stale {
  margin-top: 0;
}

.proofreading-waiting__cat {
  width: 120px;
  height: 120px;
  margin-bottom: 20px;

  :deep(.cat-icon) {
    width: 120px;
    height: 120px;
  }
}

.proofreading-waiting__spinner {
  margin: 0 0 16px;
  padding: 0;
}

.proofreading-waiting__message {
  margin: 0 0 20px;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.proofreading-waiting__message--failed {
  margin-bottom: 12px;
}

.proofreading-waiting__message--stale {
  white-space: pre-line;
}

.proofreading-waiting__status {
  margin: -20px 0 32px;
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}

.proofreading-waiting__error {
  margin: 0 0 24px;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-warning-text);
}

.proofreading-waiting__cancel-button,
.proofreading-waiting__back-button {
  min-width: 220px;
  border: none;
  border-radius: 999px;
  padding: 12px 28px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
}

.proofreading-waiting__cancel-button {
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
}

.proofreading-waiting__back-button {
  background-color: var(--color-primary);
  color: var(--color-text-invert);
}

@media (max-width: 576px) {
  .proofreading-waiting__cat {
    width: 100px;
    height: 100px;

    :deep(.cat-icon) {
      width: 100px;
      height: 100px;
    }
  }
}
</style>
